import type { AuthMeResponse } from '@cult/schemas';
import type { QueryClient } from '@tanstack/react-query';
import { Link, Outlet, createFileRoute, redirect, useRouterState } from '@tanstack/react-router';
import { Alert, Flex, Layout, Spin, Typography } from 'antd';
import { DevBadge } from '../components';
import { UserMenu, authKeys, buildSilentResyncUrl, loadAuthMe } from '../features/auth';
import { CartButton } from '../features/market';
import { useOrgConfigQuery } from '../features/org-config';
import { useCurrentUser } from '../state/use-current-user';
import { useOrgConfig } from '../state/use-org-config';
import styles from './_auth.module.scss';

const { Header, Content } = Layout;
const { Title } = Typography;

const AuthLayout = () => {
  const { config } = useOrgConfig();
  const orgQuery = useOrgConfigQuery();
  const user = useCurrentUser((s) => s.user);
  // The market list page (/market) now carries its own cart button in the filter row; only the
  // detail page (/market/$number) — which has no filter row — still needs the header cart.
  const isMarketDetail = useRouterState({
    select: (s) => s.location.pathname.startsWith('/market/'),
  });

  return (
    <Layout className={styles.layout}>
      <Header className={styles.header}>
        <Flex className={styles.nav}>
          <Link to="/home" className={styles.logo}>
            {config.logoUrl ? (
              <img src={config.logoUrl} alt={config.orgName} className={styles.logoImg} />
            ) : (
              <Title level={4} className={styles.logoTitle}>
                {config.orgName}
              </Title>
            )}
            {orgQuery.isPending ? <Spin size="small" /> : null}
          </Link>
        </Flex>
        {user ? (
          <div className={styles.actions}>
            {isMarketDetail ? <CartButton /> : null}
            <UserMenu user={user} />
          </div>
        ) : null}
      </Header>
      {orgQuery.error ? (
        <Alert
          type="warning"
          message="Couldn't load org settings — using defaults."
          showIcon
          closable
          className={styles.alert}
        />
      ) : null}
      <Content>
        <Outlet />
      </Content>
      <DevBadge />
    </Layout>
  );
};

export const Route = createFileRoute('/_auth')({
  beforeLoad: async ({ context, location }: { context: { queryClient: QueryClient }; location: { href: string } }) => {
    const me = await context.queryClient.ensureQueryData<AuthMeResponse | null>({
      queryKey: authKeys.me(),
      queryFn: loadAuthMe,
      staleTime: 60_000,
    });
    if (!me) throw redirect({ to: '/login' });
    // Server says the profile is stale: leave the SPA for a prompt=none round-trip through
    // Google. The API redirects straight back to this page with the fresh name/avatar, or —
    // if Google needs interaction — back here unchanged, and won't ask again for a while.
    if (me.profileSyncRequired) throw redirect({ href: buildSilentResyncUrl(location.href), reloadDocument: true });
    useCurrentUser.getState().setUser(me.user);
  },
  component: AuthLayout,
});
